import React from 'react';
import { useToasts } from '@applyboard/crystal-ui';
import { useStaticProps } from '../../context';
import { useUpdateCourseMappingActivity } from './useUpdateCourseMappingActivity';
import { CourseMappingDialogForm, CourseMappingFormData } from '../CourseMappingDialogForm';
import type { CourseMappingResource } from '@ab/ams-school-api/types/private/v1';
import { getApiErrorMessage } from '~/utils/getApiErrorMessage';

export function EditCourseMappingDialog(props: { courseMapping: CourseMappingResource; onClose: () => void }) {
  const { referenceData } = useStaticProps();
  const activity = useUpdateCourseMappingActivity();
  const toasts = useToasts();
  const { courseMapping } = props;

  // Transform course mapping data to form initial values
  const initialValues: CourseMappingFormData = React.useMemo(
    () => ({
      sourceCountryCode: courseMapping.attributes.sourceCountry?.alpha2Code ?? '',
      sourceEducationLevel: courseMapping.attributes.sourceEducationLevel ?? '',
      sourceCourseName: courseMapping.attributes.sourceCourseName ?? '',
      sourceInstitutionName: courseMapping.attributes.sourceInstitutionName ?? '',
      equivalentCourse: courseMapping.attributes.equivalentCourse ?? '',
    }),
    [courseMapping]
  );

  // Prepare dropdown options
  const countries = React.useMemo(
    () =>
      Object.entries(referenceData.countries)
        .map(([code, value]) => ({
          label: value.countryLabel,
          value: code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [referenceData.countries]
  );

  const educationLevels = React.useMemo(
    () =>
      Object.entries(referenceData.educationLevels)
        .map(([id, value]) => ({
          label: value.name,
          value: id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [referenceData.educationLevels]
  );

  const courses = React.useMemo(
    () =>
      Object.entries(referenceData.courses)
        .map(([id, value]) => ({
          label: `${value.code} - ${value.name}`,
          value: id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [referenceData.courses]
  );

  const handleSubmit = React.useCallback(
    (formData: CourseMappingFormData) => {
      activity
        .update({
          id: courseMapping.id,
          equivalentCourse: formData.equivalentCourse,
          sourceCourseName: formData.sourceCourseName,
          sourceCountryCode: formData.sourceCountryCode || null,
          sourceEducationLevel: formData.sourceEducationLevel || null,
          sourceInstitutionName: formData.sourceInstitutionName || null,
        })
        .then(() => {
          toasts.positive('Successfully updated course mapping');
          props.onClose();
        })
        .catch((error) => {
          toasts.negative(
            new Error(getApiErrorMessage(error, 'Failed to update course mapping. Please try again.'), {
              cause: error,
            })
          );
        });
    },
    [activity, courseMapping.id, toasts, props]
  );

  return (
    <CourseMappingDialogForm
      dialogTitle="Edit Course Mapping"
      submitButtonText="Update Mapping"
      initialValues={initialValues}
      isBusy={activity.isBusy}
      onSubmit={handleSubmit}
      onCancel={props.onClose}
      countries={countries}
      educationLevels={educationLevels}
      courses={courses}
    />
  );
}
